/**
 * Shape contracts shared between mock data, the API client, and components.
 * The mock module (web/src/mocks/*) defines the structures; the live API path
 * must return the same shape.
 */

import type { EngagementScope } from '../../src/reports/shared/engagement-scope';
import type { DashboardRole, MemberStatus, TeamActivityEntry } from '../../src/services/team/roles';

export interface ReportRow {
  id: string;
  account: string | null;
  template: string | null;
  affiliate: string | null;
  coachee: string | null;
  status: 'Completed' | 'Active' | 'Cancelled' | string;
  sowStart: string | null;
  sowEnd: string | null;
  hBudget: number | null;
  hIncurred: number | null;
  oppNum: string | null;
  milestone: string | null;
  dateInc: string | null;
  created: string | null;
  hours: number | null;
  rate: number | null;
  feeBudget: number | null;
  clientFee: number | null;
  oppId: string | null;
  projectId: string | null;
  engagementId: string | null;
  sessionId: string | null;
  // V–AG NetSuite-prep columns (optional: present on live rows, absent on the
  // design mock literal unless enriched). Mirror FinanceReportRow.
  cogs?: number | null;
  overBudget?: string | null;
  ratePct?: number | null;
  coacheeLast?: string | null;
  coacheeLastDerived?: boolean;
  lineMemo?: string | null;
  refMemo?: string | null;
  refMemoLen?: number | null;
  truncatedName?: string | null;
  vendorName?: string | null;
  internalId?: string | null;
  hoursRevenue?: number | null;
  expiration?: string | null;
  enrichedFields?: string[];
  // Sent-to-finance stamps (optional: absent on the design mock literal unless
  // enriched). 'sent_changed' = differs from what Finance last received.
  sentStatus?: SentStatus;
  sentAt?: string | null;
  sentBatchId?: string | null;
  sentSource?: SentSource | null;
  sentChangedFields?: string[];
}

export type SentStatus = 'unsent' | 'sent' | 'sent_changed';
export type SentSource = 'upload' | 'paste';

/** One recorded send to Finance (file upload or pasted ID list). */
export interface SendBatch {
  id: string;
  sentAt: string;
  recordedByEmail: string | null;
  source: SentSource;
  originalFilename: string | null;
  storagePath: string | null;
  sessionCount: number;
  newCount: number | null;
  resentCount: number | null;
  unmatchedCount: number | null;
  note: string | null;
}

export interface RecordSendResult {
  batchId: string;
  counts: {
    total: number;
    newSessions: number;
    previouslySent: number;
    notFoundInReports: number;
  };
  rowsRestamped: number;
  skippedRowNumbers?: number[];
  duplicateSessionIds?: string[];
  sheetName?: string;
}

export type GenerationStatus = 'Succeeded' | 'Failed' | 'Running';

export interface Generation {
  id: string;
  generatedAt: string;
  rangeStart: string;
  rangeEnd: string;
  rows: number;
  user: string;
  status: GenerationStatus;
}

export type ColumnType = 'text' | 'date' | 'num' | 'cur' | 'id' | 'pill';
export type ColumnAlign = 'left' | 'center' | 'right';

export interface ColumnSpec {
  id: keyof ReportRow;
  label: string;
  w: number;
  type: ColumnType;
  frozen?: boolean;
  frozenLast?: boolean;
  align?: ColumnAlign;
  primary?: boolean;
  overBar?: boolean;
}

export interface ReportFilters {
  account: string[];
  affiliate: string[];
  coachee: string;
  status: string[];
  opp: string;
  sent: SentStatus[];
}

export const EMPTY_FILTERS: ReportFilters = {
  account: [],
  affiliate: [],
  coachee: '',
  status: [],
  opp: '',
  sent: [],
};

// ============================================================
// Engagement Health
// ============================================================

export type AlertType = 'stalled_start' | 'near_completion' | 'stale_activity' | 'stale_activity_30d';
export type RiskTier = 'not_scored' | 'healthy' | 'watch' | 'at_risk' | 'critical';

export interface RiskSignal {
  type: AlertType;
  points: number;
  detail: Record<string, unknown>;
}

export interface AlertSnooze {
  snoozedUntil: string;
  snoozedByEmail: string | null;
}

export interface DeliveringAffiliateRef {
  name: string | null;
  email: string | null;
}

export interface AlertRow {
  id: string;
  opportunityId: string;
  opportunityName: string | null;
  accountName: string | null;
  accountRollupName: string | null;
  accountDisplayName: string | null;
  ownerId: string | null;
  ownerName: string | null;
  ownerEmail: string | null;
  csmId: string | null;
  csmName: string | null;
  csmEmail: string | null;
  projectNumbers: string[];
  coacheeName: string | null;
  sowStartDate: string | null;
  sowEndDate: string | null;
  contractedHours: number | null;
  loggedHours: number;
  pctComplete: number | null;
  lastSessionDate: string | null;
  daysSinceLastSession: number | null;
  daysSinceStart: number | null;
  engagementStatus: string | null;
  deliveringAffiliates: DeliveringAffiliateRef[];
  alertType: AlertType;
  snooze: AlertSnooze | null;
}

export interface EngagementAssessment {
  opportunityId: string;
  opportunityName: string | null;
  accountName: string | null;
  accountRollupName: string | null;
  accountDisplayName: string | null;
  ownerId: string | null;
  ownerName: string | null;
  ownerEmail: string | null;
  csmId: string | null;
  csmName: string | null;
  csmEmail: string | null;
  projectNumbers: string[];
  coacheeName: string | null;
  sowStartDate: string | null;
  sowEndDate: string | null;
  contractedHours: number | null;
  loggedHours: number;
  pctComplete: number | null;
  lastSessionDate: string | null;
  daysSinceLastSession: number | null;
  daysSinceStart: number | null;
  daysUntilEnd: number | null;
  engagementType: string | null;
  engagementStatus: string | null;
  deliveringAffiliates: DeliveringAffiliateRef[];
  riskScore: number | null;
  riskTier: RiskTier;
  riskSignals: RiskSignal[];
}

export interface EngagementHealthPayload {
  snapshot: EngagementSnapshot | null;
  alerts: AlertRow[];
  engagements: EngagementAssessment[];
  requiresRefresh: boolean;
}

export interface AffiliateRollup {
  name: string;
  email: string | null;
  stalledStart: number;
  nearCompletion: number;
  staleActivity: number;
  staleActivity30d: number;
  total: number;
  lastAlertAt: string | null;
}

export interface EngagementSnapshot {
  id: string;
  generatedAt: string;
  opportunitiesScanned: number;
  counts: {
    stalledStart: number;
    nearCompletion: number;
    staleActivity: number;
    staleActivity30d: number;
  };
}

export type EngagementDigestDeliveryMode = 'disabled' | 'test' | 'live';

export interface EngagementDigestConfig {
  deliveryMode: EngagementDigestDeliveryMode;
  liveSendEnabled: boolean;
  testSendEnabled: boolean;
  testRecipient: string | null;
  cronEnabled: boolean;
  cron: string;
  timezone: string;
  nextScheduledRunAt: string | null;
  excludedAccountRollups: string[];
  sender: string;
}

export interface EngagementDigestRun {
  id: string;
  snapshot_id: string | null;
  period_key: string;
  trigger: 'manual' | 'cron';
  status: 'running' | 'succeeded' | 'failed';
  recipients_count: number;
  engagements_count: number;
  blocked_count: number;
  excluded_count: number;
  sent_count: number;
  test_sent_count: number;
  failed_count: number;
  error_message: string | null;
  generated_at: string;
  completed_at: string | null;
}

export interface EngagementDigestContentRow {
  opportunityId: string;
  opportunityName: string | null;
  engagementStatus: string | null;
  accountName: string | null;
  accountDisplayName: string | null;
  coacheeName: string | null;
  projectNumbers: string[];
  affiliateNames: string[];
  roles: Array<'opportunity_owner' | 'csm'>;
  contractedHours: number | null;
  loggedHours: number;
  hoursRemaining: number | null;
  pctComplete: number | null;
  sowEndDate: string | null;
  lastSessionDate: string | null;
  riskSignals: RiskSignal[];
  salesforceUrl: string;
}

export interface EngagementDigestMessage {
  id: string;
  run_id: string;
  period_key: string;
  recipient_name: string | null;
  intended_recipient_email: string | null;
  roles: Array<'opportunity_owner' | 'csm'>;
  subject: string;
  body_html: string;
  body_text: string;
  dashboard_url: string;
  engagement_ids: string[];
  content: EngagementDigestContentRow[];
  status: 'preview' | 'blocked' | 'test_sent' | 'sent' | 'failed';
  blocked_reason: string | null;
  resend_message_id: string | null;
  last_delivery_mode: 'test' | 'live' | null;
  last_attempt_at: string | null;
  sent_at: string | null;
  error_message: string | null;
}

export interface EngagementDigestRunDetail {
  run: EngagementDigestRun;
  messages: EngagementDigestMessage[];
  deliveries: Array<Record<string, unknown>>;
}

export type AlertFilter = 'all' | AlertType;

// ============================================================
// Coach outreach
// ============================================================

export type OutreachWorkflow = 'coach_statement' | 'stalled_start' | 'stale_activity_30d' | 'stale_activity';
export type OutreachDeliveryMode = 'disabled' | 'test' | 'live';

export interface OutreachRuntimeConfig {
  deliveryMode: OutreachDeliveryMode;
  testRecipient: string | null;
  liveSendEnabled: boolean;
  inboundEmailEnabled: boolean;
  replyParsingEnabled: boolean;
  replyDomain: string;
}

export type OutreachDraftStatus =
  | 'pending_review'
  | 'edited'
  | 'approved'
  | 'sending'
  | 'sent'
  | 'awaiting_reply'
  | 'replied'
  | 'closed'
  | 'rejected'
  | 'skipped'
  | 'blocked'
  | 'failed';

export interface OutreachEngagement {
  engagementStatus?: string | null;
  accountDisplayName?: string | null;
  opportunityId: string;
  opportunityName: string | null;
  accountName: string | null;
  coacheeName: string | null;
  projectNumber: string | null;
  hoursContracted: number | null;
  hoursCompleted: number | null;
  hoursRemaining: number | null;
  sowStartDate: string | null;
  sowEndDate: string | null;
  lastSessionDate: string | null;
  daysSinceLastSession: number | null;
  daysSinceStart: number | null;
  sourceAlertType: string | null;
  ledgerNote: string | null;
}

export interface OutreachDraft {
  id: string;
  runId: string;
  workflow: OutreachWorkflow;
  recipientName: string | null;
  recipientEmail: string | null;
  coachName: string | null;
  coachEmail: string | null;
  subject: string;
  bodyMd: string;
  status: OutreachDraftStatus;
  reviewedByEmail: string | null;
  reviewedAt: string | null;
  rejectReason: string | null;
  engagements: OutreachEngagement[];
}

export interface OutreachRunSummary {
  generationScope?: EngagementScope | null;
  id: string;
  generatedAt: string;
  workflow: OutreachWorkflow;
  draftsCount: number;
  engagementsCount: number;
  suppressedCount: number;
}

export interface OutreachPayload {
  run: OutreachRunSummary | null;
  drafts: OutreachDraft[];
}

export interface ReplyParseEngagement {
  mentioned_coachee_names: string[];
  mentioned_client_names: string[];
  categories: string[];
  delay_reason: string | null;
  promised_log_date: string | null;
  suggested_status_change: string | null;
  suppress_until: string | null;
  engagement_confidence: number;
  matched_opportunity_id?: string | null;
  matched_account_name?: string | null;
  matched_coachee_name?: string | null;
  deterministic_match_confidence?: number;
  match_needs_human?: boolean;
}

export interface ReplyParse {
  id: string;
  summary: string;
  overall_sentiment: string;
  needs_human: boolean;
  confidence: number;
  per_engagement: ReplyParseEngagement[];
  model: string;
  parsed_at: string;
}

/** Danielle's verdict on a parsed engagement's labels. */
export type ReviewStatus = 'unverified' | 'confirmed' | 'edited';
export type ReplyQueueStatus = 'new' | 'follow_up' | 'acknowledged' | 'dismissed';

export interface ReplyLabelOverride {
  parseId: string;
  engagementIndex: number;
  categories: string[];
}

export interface ReplyReview {
  reply_id: string;
  queue_status: ReplyQueueStatus;
  note: string | null;
  follow_up_owner: string | null;
  follow_up_owner_email: string | null;
  follow_up_due_at: string | null;
  label_overrides: ReplyLabelOverride[];
  reviewed_by_email: string | null;
  reviewed_at: string | null;
  resolved_at: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * One accumulating context entry per matched engagement — the human-verified
 * memory the AI parse seeds and Danielle confirms/edits. Self-describing
 * (account + coachee) so it reads cleanly in the inbox and the coach drawer.
 */
export interface LedgerEntry {
  id: string;
  opportunity_id: string;
  account_name: string | null;
  coachee_name: string | null;
  category: string | null;
  categories: string[];
  body: string;
  promised_log_date: string | null;
  review_status: ReviewStatus;
  reviewed_by_email: string | null;
  reviewed_at: string | null;
  created_at: string;
}

export interface InboundReplyRow {
  id: string;
  from_email: string | null;
  from_name: string | null;
  subject: string | null;
  classification: string;
  match_method: string | null;
  match_confidence: number | null;
  status: string;
  stripped_text: string | null;
  received_at: string | null;
  created_at: string;
  delivery_mode: 'test' | 'live' | null;
  delivered_to_email: string | null;
  intended_to_email: string | null;
  parse: ReplyParse | null;
  review: ReplyReview | null;
  // Per-engagement verified context rows for this reply's latest parse (matched
  // engagements only). Empty when nothing correlated or parsing hasn't run.
  ledger: LedgerEntry[];
}

// ============================================================
// SOW generator
// ============================================================

export type SowTemplateType = 'coaching_sow' | 'cohort_contract' | 'exec_coaching_sow' | 'executive_partner_sow';

export type SowExecCoachingScope = 'exec_coaching' | 'c_suite' | 'executive_partner' | 'hybrid';

export type SowExecutivePartnerVariant = 'ep_only' | 'hybrid_ep' | 'hybrid_coach';

export interface SowPersonBlock {
  name: string;
  title?: string;
  email: string;
  phone: string;
}

export interface SowCoachingPhase {
  name: string;
  description: string;
  hours: number;
  hoursNote?: string;
}

export interface SowCoachingFields {
  templateType: 'coaching_sow';
  tierLabel: string;
  clientCompany: string;
  accountOwner: SowPersonBlock;
  csm: SowPersonBlock;
  coachee: SowPersonBlock;
  netsuiteProjectNumber: string;
  engagementLocation: string;
  contractedHours: number;
  timeframe: string;
  coachName: string;
  coachHourlyRate: number;
  feesTotal: number;
  desiredOutcomes: string;
  phases: SowCoachingPhase[];
  projectApproach: string;
  specialInstructions: string;
}

export interface SowServiceLine {
  title: string;
  deliveryLine: string;
  price: number;
}

export interface SowServiceGroup {
  title: string;
  subtitle: string;
  lines: SowServiceLine[];
}

export interface SowCohortFields {
  templateType: 'cohort_contract';
  deliveryMode: 'in_person' | 'virtual';
  jobNumber: string;
  contractDate: string;
  effectiveDate: string;
  clientLegalName: string;
  groups: SowServiceGroup[];
  total: number;
  poRequired: boolean;
  poNumber?: string;
  billingName: string;
  billingEmail: string;
  billingAddress: string;
  billingCityStateZip: string;
  billingCountry: string;
}

export interface SowExecProgramLine {
  label: string;
  amountText: string;
}

export interface SowExecCoachingFields {
  templateType: 'exec_coaching_sow';
  scope: SowExecCoachingScope;
  tierLabel: string;
  clientCompany: string;
  oNumber: string;
  contractDate: string;
  leaderName: string;
  coachName: string;
  epName: string;
  serviceName: string;
  servicesHeading: string;
  servicesIntro: string;
  desiredOutcomes: string;
  showPhaseHours: boolean;
  phases: SowCoachingPhase[];
  programLines: SowExecProgramLine[];
  additionalFeesText: string;
  budgetTimingText: string;
  extensionText: string;
  projectTermsText: string;
  accountManagerLine: string;
}

export interface SowExecutivePartnerFields {
  templateType: 'executive_partner_sow';
  variant: SowExecutivePartnerVariant;
  clientCompany: string;
  accountOwner: SowPersonBlock;
  csm: SowPersonBlock;
  client: SowPersonBlock;
  clientAdmin: SowPersonBlock;
  netsuiteProjectNumber: string;
  stakeholder1: SowPersonBlock;
  stakeholder2: SowPersonBlock;
  engagementLocation: string;
  ep: SowPersonBlock;
  epContractedTime: string;
  epTimeframe: string;
  coach: SowPersonBlock;
  coachContractedTime: string;
  coachTimeframe: string;
  feesText: string;
  desiredOutcomes: string;
  projectApproach: string;
  specialInstructions: string;
}

export type SowFieldValues =
  | SowCoachingFields
  | SowCohortFields
  | SowExecCoachingFields
  | SowExecutivePartnerFields;

export interface SowGap {
  field: string;
  message: string;
}

export interface SowAccountContact {
  id: string;
  name: string | null;
  title: string | null;
  email: string | null;
  phone: string | null;
}

export interface SowPrefill {
  suggestedTemplate: SowTemplateType;
  detection: { template: SowTemplateType; reason: string; confidence: 'high' | 'medium' | 'low' };
  fields: SowFieldValues;
  gaps: SowGap[];
  meta: {
    opportunity: {
      id: string;
      name: string | null;
      number: string | null;
      accountName: string | null;
      stageName: string | null;
      type: string | null;
      amount: number | null;
    };
    accountContacts: SowAccountContact[];
    /** Standard phases per tier ("Tier 1".."Tier 4"), so the editor can swap
     * tiers without re-querying. Absent in mock mode. */
    coachingTierPresets?: Record<string, SowCoachingPhase[]>;
    /** Standard Desired Outcomes boilerplate, restored when picking a tier. */
    standardDesiredOutcomes?: string;
    /** Canonical drafts for each client-facing scope. */
    execCoachingScopeDefaults: Record<SowExecCoachingScope, SowExecCoachingFields>;
  };
}

export interface SowOpportunityHit {
  id: string;
  number: string | null;
  name: string | null;
  accountName: string | null;
  stageName: string | null;
  type: string | null;
  closeDate: string | null;
  amount: number | null;
}

export interface SowDocumentRow {
  id: string;
  opportunitySfId: string;
  opportunityNumber: string | null;
  opportunityName: string | null;
  accountName: string | null;
  templateType: SowTemplateType;
  version: number;
  fileName: string | null;
  generatedByEmail: string | null;
  createdAt: string;
  fieldValues?: SowFieldValues;
}

// ---------------------------------------------------------------------------
// Chemistry checks
// ---------------------------------------------------------------------------
export type ChemistryTrackState =
  | 'waiting_client'
  | 'waiting_coach'
  | 'ready_to_schedule'
  | 'scheduled'
  | 'completion_unconfirmed'
  | 'complete'
  | 'needs_human';
export type ChemistryCaseState =
  | 'new'
  | 'collecting'
  | 'scheduled'
  | 'awaiting_completion'
  | 'awaiting_selection'
  | 'needs_human'
  | 'closed';
export interface ChemistryAvailabilityWindow {
  party: 'client' | 'coach';
  /** The sender's words, verbatim — always shown next to the normalized value. */
  sourceText: string;
  startIso: string;
  endIso: string;
  timezone: string; // IANA
  status: 'valid' | 'consumed' | 'expired';
}
export type ChemistryTimelineKind =
  | 'email_out'
  | 'email_in'
  | 'parse'
  | 'match'
  | 'event_created'
  | 'event_cancelled'
  | 'reminder'
  | 'escalation'
  | 'prompt'
  | 'proposal'
  | 'note';
export interface ChemistryTimelineEvent {
  at: string;
  actor: 'agent' | 'pm' | 'client' | 'assistant' | 'coach' | 'system';
  kind: ChemistryTimelineKind;
  title: string;
  detail?: string;
  /** Shadow mode: the agent proposes this action but nothing was sent. */
  shadow?: boolean;
}
export interface ChemistryTrack {
  id: string;
  coachName: string;
  coachEmail: string;
  coachTimezone: string | null;
  state: ChemistryTrackState;
  waitingOn: string;
  nextAction: string;
  nextActionAt: string;
  /** Verified overlap awaiting the PM's Outlook invite (ready_to_schedule). */
  proposedStart?: string | null;
  proposedEnd?: string | null;
  scheduledStart: string | null;
  scheduledEnd: string | null;
  availability: ChemistryAvailabilityWindow[];
}
export interface ChemistryCase {
  id: string;
  tag: string; // e.g. CC-0142 — appears in subjects + event descriptions
  clientName: string;
  clientEmail: string;
  clientTimezone: string;
  assistantName: string | null;
  assistantEmail: string | null;
  pmName: string;
  pmEmail: string;
  state: ChemistryCaseState;
  targetDate: string;
  createdAt: string;
  waitingOn: string;
  nextAction: string;
  nextActionAt: string;
  escalationReason: string | null;
  tracks: ChemistryTrack[];
  timeline: ChemistryTimelineEvent[];
  /** Created live during this session via the Simulate flow (demo only). */
  live?: boolean;
}
export interface ChemistryCaseCreateInput {
  client: {
    name: string;
    email: string;
    timezone: string;
  };
  coaches: Array<{
    name: string;
    email: string;
    timezone: string;
  }>;
  targetDate: string;
  assistant?: {
    name: string;
    email: string;
  };
}
export interface ChemistryRuntime {
  sendEnabled: boolean;
  meetingMinutes: number;
}
// --- Simulate panel (live parse against the demo endpoint) ---
export interface ChemistryKickoffParse {
  summary: string;
  clientName: string | null;
  clientEmail: string | null;
  clientTimezone: string | null;
  assistantName: string | null;
  assistantEmail: string | null;
  pmName: string | null;
  targetDate: string | null;
  coaches: Array<{ name: string; email: string | null }>;
  /** Required kickoff fields the email did not contain → case would open in Needs human. */
  missingFields: string[];
  needsHuman: boolean;
  confidence: number;
}
export interface ChemistryParsedWindow {
  sourceText: string;
  startIso: string | null;
  endIso: string | null;
  timezone: string | null;
  /** False when the phrase is not precise enough to book (e.g. "Thursday afternoon"). */
  precise: boolean;
  note: string | null;
}
export interface ChemistryShadowEmail {
  to: string;
  subject: string;
  body: string;
}
export interface ChemistryAvailabilityParse {
  summary: string;
  windows: ChemistryParsedWindow[];
  needsClarification: boolean;
  clarificationQuestion: string | null;
  proposedMatch: {
    startIso: string;
    endIso: string;
    timezone: string;
    rationale: string;
  } | null;
  draftEmails: ChemistryShadowEmail[];
  needsHuman: boolean;
  confidence: number;
}
export type ChemistrySimulateRequest =
  | { mode: 'kickoff'; text: string }
  | {
      mode: 'availability';
      text: string;
      context: {
        caseTag: string;
        from: 'client' | 'coach';
        fromName: string;
        clientName: string;
        clientEmail: string;
        clientTimezone: string;
        targetDate: string;
        coachName: string | null;
        /** Windows already on file for the counterparty, so a match can be proposed. */
        knownWindows: Array<{ party: 'client' | 'coach'; startIso: string; endIso: string; timezone: string }>;
      };
    };
export interface ChemistrySimulateResponse {
  mode: 'kickoff' | 'availability';
  kickoff?: ChemistryKickoffParse;
  availability?: ChemistryAvailabilityParse;
  /** True when the live call failed and a canned example is shown instead. */
  degraded: boolean;
  model: string | null;
}

// ============================================================
// Settings › Team
// ============================================================


export type { DashboardRole, MemberStatus, TeamActivityEntry };

export interface TeamMember {
  id: string;
  email: string;
  name: string | null;
  role: DashboardRole;
  status: MemberStatus;
  lastSignInAt: string | null;
  invitedAt: string | null;
  invitedByName: string | null;
  deactivatedAt: string | null;
  external: boolean;
}

export interface TeamPayload {
  members: TeamMember[];
  activity: TeamActivityEntry[];
  orgDomains: string[];
}

export interface TeamInviteResult {
  email: string;
  outcome: 'invited' | 'skipped' | 'failed';
  message?: string;
}
